// WHAT: Component for displaying success and error messages
// WHY:  Separates message UI from main component to reduce complexity
// DOMAIN: Settings → Yazio
// DEPENDENCIES: React, @/components/ui, lucide-react

import { AlertCircle, CheckCircle, Loader2 } from "lucide-react";
import React from "react";

import { Card, Stack } from "@/components/ui";

interface StatusMessagesProperties {
  /** Error message to display */
  error?: string;
  /** Whether component is in loading state */
  loading: boolean;
  /** Success message to display */
  success?: string;
}

/**
 * WHAT:
 *   Component for displaying success and error messages.
 *
 * WHY:
 *   Separates message UI from main component to reduce complexity.
 *
 * @param root0
 *
 * @param root0.error
 *
 * @param root0.loading
 *
 * @param root0.success
 *
 * @returns {JSX.Element} Status messages component
 *
 */
export const StatusMessages: React.FC<StatusMessagesProperties> = ({ error, loading, success }) => {
  return (
    <>
      {success ? (
        <Card className="bg-green-500/10 border-green-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <CheckCircle className="w-5 h-5 text-green-400" />
            <span className="text-green-300 text-sm font-medium">{success}</span>
          </Stack>
        </Card>
      ) : null}

      {error ? (
        <Card className="bg-red-500/10 border-red-500/20" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <AlertCircle className="w-5 h-5 text-red-400" />
            <span className="text-red-300 text-sm font-medium">{error}</span>
          </Stack>
        </Card>
      ) : null}

      {loading ? (
        <Card className="bg-slate-700/50 border-slate-600/50" padding="md" variant="default">
          <Stack align="center" direction="row" gap={3}>
            <Loader2 className="w-5 h-5 text-slate-400 animate-spin" />
            <span className="text-slate-400 text-sm">Loading Yazio configuration...</span>
          </Stack>
        </Card>
      ) : null}
    </>
  );
};

